import React, {useState, useEffect, useMemo} from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Tooltip,
    Button,
    IconButton,
    Stack,
    Chip,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AddLinkIcon from '@mui/icons-material/AddLink';
import ClearIcon from '@mui/icons-material/Clear';
import { Globe } from 'lucide-react';
import AddNodeForm from './AddNodeForm';
import { NodeConfig } from "../utils/MeldUtils";
import { useShimmer } from './ShimmerContext';
import ShimmerHighlight from './ShimmerHighlight';
import { LinkTypes } from '../utils/MeldUtils';
import {useTranslation} from "react-i18next";
import i18n from "i18next";
import { useMELTranslations } from "../hooks/useMELTranslations";
import { useLanguage } from "../utils/LanguageContext";

const NodeInfoPanel = ({
                           selectedModel,
                           selectedEvidence,
                           links,
                           linkStatus,
                           onUpdateNode,
                           onAddReference,
                           onUpdateLink,
                           onDelete,
                           onAddNode,
                           toggleInfoPanel
                       }) => {
    const [editableModel, setEditableModel] = useState(null);
    const [editableEvidence, setEditableEvidence] = useState(null);
    const [showAddRef, setShowAddRef] = useState(false);
    const [newRef, setNewRef] = useState('');
    const { activeShimmer } = useShimmer();
    const { t } = useTranslation();
    const { currentLanguage, getNodeTranslation, updateTrigger } = useLanguage();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isTranslating } = useMELTranslations(selectedModel || selectedEvidence);
    const languageContext = useMemo(() => {
        return  {
            currentLanguage,
            getNodeTranslation,
        };
    }, [currentLanguage, getNodeTranslation]);


    const isTranslated = i18n.language !== 'en';
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: () => {}
    });

    useEffect(() => {
        if (selectedModel || selectedEvidence) {
            // Force re-render of form fields
            setEditableModel(selectedModel ? { ...selectedModel } : null);
            setEditableEvidence(selectedEvidence ? { ...selectedEvidence } : null);
        }
    }, [selectedModel, selectedEvidence, currentLanguage, updateTrigger]);


    const ConfirmDialog = ({ open, title, message, onConfirm, onClose }) => (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>{message}</DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('info.cancel')}</Button>
                <Button onClick={onConfirm} color="error" autoFocus>{t('info.delete')}</Button>
            </DialogActions>
        </Dialog>
    );

    const handleNodeDelete = (nodeId) => {
        setConfirmDialog({
            open: true,
            title: t('info.titlenodedelete'),
            message: t('info.rusnodedelete'),
            onConfirm: () => {
                onDelete(nodeId);
                setConfirmDialog({ ...confirmDialog, open: false });
            }
        });
    };

    const handleReferenceDelete = (node, refIndex) => {
        setConfirmDialog({
            open: true,
            title: t('info.titlerefdelete'),
            message: t('info.rusrefdelete'),
            onConfirm: () => {
                const updatedNode = { ...node };
                const updatedRefs = [...(updatedNode.references || [])];
                updatedRefs.splice(refIndex, 1);
                updatedNode.references = updatedRefs;

                if (node.type === 'model') {
                    setEditableModel(updatedNode);
                } else {
                    setEditableEvidence(updatedNode);
                }

                onUpdateNode(updatedNode, true);
                setConfirmDialog({ ...confirmDialog, open: false });
            }
        });
    };

    const handleAddReference = (node, newReference) => {
        const updatedNode = { ...node };
        const updatedRefs = [...(updatedNode.references || []), newReference];
        updatedNode.references = updatedRefs;

        // Update the local state
        if (node.type === 'model') {
            setEditableModel(updatedNode);
        } else {
            setEditableEvidence(updatedNode);
        }

        // Call onUpdateNode with the reference-only flag
        onUpdateNode(updatedNode, true);

        // Clear the input and hide the form
        setNewRef('');
        setShowAddRef(false);
    };

    const renderTextField = (node, field, label) => {
        // Get the appropriate display value based on the field type
        const getDisplayValue = () => {
            if (currentLanguage === 'en') {
                return node[field];
            }

            return field === 'id'
                ? NodeConfig.getDisplayText(node, languageContext)
                : NodeConfig.getDisplayDescription(node, languageContext);

        };

        return (
            <TextField
                fullWidth
                label={label}
                value={getDisplayValue()}
                onChange={(e) => onUpdateNode({ ...node, [field]: e.target.value })}
                multiline={field === 'description'}
                rows={field === 'description' ? 3 : 1}
                disabled={currentLanguage !== 'en'}
                size="small"
                sx={{
                    mb: 1,
                    '& .Mui-disabled': {
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                        color: 'rgba(0, 0, 0, 0.87)',
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                        color: 'rgba(0, 0, 0, 0.87)',
                    }
                }}
                InputProps={{
                    readOnly: currentLanguage !== 'en',
                    ...(field === 'id' && {
                        inputProps: { maxLength: 25 }
                    })
                }}
            />
        );
    };

    const renderNodeInfo = (node) => {
        if (!node) return null;
        const editableNode = node.type === 'model' ? editableModel : editableEvidence;
        if (!editableNode) return null;

        return (
            <ShimmerHighlight
                isActive={activeShimmer === 'infoPanel'}
                style={{ position: 'relative', overflow: 'hidden' }}
            >
            <Paper sx={{ p: 1, mb: 1 }} elevation={0}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1
                }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {node.type.charAt(0).toUpperCase() + node.type.slice(1)}
                    </Typography>
                    {isTranslated && (
                        <Tooltip title={t('info.translated')}>
                            <Globe size={20} />
                        </Tooltip>
                    )}

                    {node.type === 'model' && (
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            [{t('info.plausibility')}: {node.plausibility !== undefined ? node.plausibility : 0}]
                        </Typography>
                    )}
                    <IconButton
                        size="small"
                        onClick={() => handleNodeDelete(node.id)}
                        color="error"
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>

                {renderTextField(node, 'id', `${node.type} ${t('info.title')}`)}
                {renderTextField(node, 'description', `${node.type} ${t('info.description')}`)}

                <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ gap: 1 }}>
                    {(editableNode.references || []).map((url, index) => (
                        <Chip
                            key={index}
                            label={`[${index + 1}]`}
                            onClick={() => window.open(url, '_blank')}
                            onDelete={() => handleReferenceDelete(editableNode, index)}
                            deleteIcon={<ClearIcon fontSize="small" />}
                            variant="outlined"
                            size="small"
                            sx={{
                                height: 24,
                                '& .MuiChip-deleteIcon': {
                                    fontSize: '16px',
                                    '&:hover': {
                                        color: 'error.main'
                                    }
                                }
                            }}
                        />
                    ))}
                    <IconButton
                        size="small"
                        onClick={() => setShowAddRef(true)}
                        color="primary"
                        sx={{ ml: 0.5 }}
                    >
                        <AddLinkIcon fontSize="small" />
                    </IconButton>
                </Stack>

                {showAddRef && (
                    <Box sx={{ mt: 1 }}>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            if (newRef) {
                                handleAddReference(editableNode, newRef);
                            }
                        }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={newRef}
                                    onChange={(e) => setNewRef(e.target.value)}
                                    placeholder="Enter URL"
                                    type="url"
                                    sx={{ flex: 1 }}
                                />
                                <Button
                                    variant="contained"
                                    size="small"
                                    type="submit"
                                    disabled={!newRef}
                                >
                                    Add
                                </Button>
                            </Box>
                        </form>
                    </Box>
                )}
            </Paper>
            </ShimmerHighlight>
        );
    };

    const renderLinkForm = () => {
        // Function to determine if a specific link type is active
        const isLinkTypeActive = (buttonType) => {
            switch(buttonType) {
                case 'supports':
                    return linkStatus === 'supports';
                case 'strongly':
                    return linkStatus === 'strongly supports';
                case 'contradicts':
                    return linkStatus === 'contradicts';
                case 'noEffect':
                    // Consider both explicit no effect and unlinked states
                    return linkStatus === 'no effect';
            }
            return false;
        };

        return (
            <ShimmerHighlight
                isActive={activeShimmer === 'link'}
                style={{ position: 'relative', overflow: 'hidden' }}
            >
                <Paper sx={{ p: 1, mb: 1 }} elevation={0}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Link Type</Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        <Button
                            variant={isLinkTypeActive('supports') ? 'contained' : 'outlined'}
                            onClick={() => onUpdateLink(LinkTypes.SUPPORTS.value)}
                            size="small"
                            sx={{ minWidth: 'auto', px: 1 }}
                        >
                            {t('info.supports')}
                        </Button>
                        <Button
                            variant={isLinkTypeActive('strongly') ? 'contained' : 'outlined'}
                            onClick={() => onUpdateLink(LinkTypes.STRONGLY_SUPPORTS.value)}
                            size="small"
                            sx={{ minWidth: 'auto', px: 1 }}
                        >
                            {t('info.strongly')}
                        </Button>
                        <Button
                            variant={isLinkTypeActive('contradicts') ? 'contained' : 'outlined'}
                            onClick={() => onUpdateLink(LinkTypes.CONTRADICTS.value)}
                            size="small"
                            color="error"
                            sx={{ minWidth: 'auto', px: 1 }}
                        >
                            {t('info.contradicts')}
                        </Button>
                        <Button
                            variant={isLinkTypeActive('noEffect') ? 'contained' : 'outlined'}
                            onClick={() => {
                                // If it's already set to no effect, clear the link entirely
                                if (linkStatus === 'no effect') {
                                    onUpdateLink(LinkTypes.NO_LINK.value);
                                } else {
                                    onUpdateLink(LinkTypes.NO_EFFECT_EXPLICIT.value);
                                }
                            }}
                            size="small"
                            color="inherit"
                            sx={{ minWidth: 'auto', px: 1 }}
                        >
                            {t('info.noeffect')}
                        </Button>
                    </Stack>
                </Paper>
            </ShimmerHighlight>
        );
    };

    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: 400,
                height: '100%',
                bgcolor: 'background.paper',
                position: 'relative',
                overflow: 'auto',
                boxSizing: 'border-box'
            }}
        >
            <IconButton
                onClick={toggleInfoPanel}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    zIndex: 1
                }}
            >
                <CloseIcon fontSize="small" />
            </IconButton>

            <Box sx={{ p: 1, pt: 5 }}>
                {selectedModel && renderNodeInfo(selectedModel)}
                {selectedModel && selectedEvidence && renderLinkForm()}
                {selectedEvidence && renderNodeInfo(selectedEvidence)}
                <AddNodeForm onAddNode={onAddNode} />
            </Box>
            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={confirmDialog.onConfirm}
                onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
            />
        </Box>
    );
};

export default NodeInfoPanel;